import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toastError, toastSuccess } from '../../../components/Alert/Alert';
import Button from '../../../components/Button/Button';
import Input from '../../../components/Input/Input';
import { DataRow } from '../../MACRA_1/components/Table/ITable';
import Table from '../../MACRA_1/components/Table/Table';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';

interface IPanelDocumentProps {}

const sizeLimit = 100 * 1048576;

const PanelDocument = (props: IPanelDocumentProps) => {
    const { t } = useTranslation();
    const [documents, setDocuments] = React.useState<DataRow[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [openDialog, setOpenDialog] = React.useState<boolean>(false);
    const [error, setError] = React.useState<boolean>(false);
    const [filename, setFilename] = React.useState<string>('');
    const [formfile, setFormfile] = React.useState<File>();

    React.useEffect(() => {
        const getAllDocuments = async () => {
            let response = await BeMaccaferriStoreHelper.getAllDocumentsAsync();
            let dataRow: DataRow[] = response.data.map((x, index) => ({ id: index, name: x.name, url: x.url, creationDate: x.lastModified }));
            setDocuments(dataRow);
        };
        setLoading(true);
        getAllDocuments();
        setLoading(false);
    }, []);

    const handleDeleteDocument = async (row: DataRow) => {
        let res = await BeMaccaferriStoreHelper.deleteDocumentAsync(row.name);
        if (res.data) {
            toastSuccess(t('recent_projects_alert_success_title'));
            let newDocument = documents.filter(x => x.name !== row.name);
            setDocuments(newDocument);
        }
    };

    const handleSaveDocument = async () => {
        let extension = formfile!.name.slice(formfile!.name.lastIndexOf('.') + 1)!;
        let filenameWithExtension = filename.concat('.' + extension);

        const formData = new FormData();
        formData.append('filename', filenameWithExtension);
        formData.append('formfile', formfile as Blob);

        let res = await BeMaccaferriStoreHelper.uploadDocumentAsync(formData);
        if (res.data) {
            let currentDocument = [...documents];
            currentDocument.push({ id: currentDocument.length, name: filenameWithExtension, creationDate: new Date() });
            setDocuments(currentDocument);
            setOpenDialog(false);
        }
    };

    return (
        <div className='panel-container'>
            <div className='nosubmit d-flex justify-content-end mb-4'>
                <button type='button' onClick={() => setOpenDialog(true)}>
                    {t('new_document')}
                </button>
            </div>

            <Table data={documents} progressPending={loading} onDeleteRow={handleDeleteDocument} isDocument />

            {openDialog && (
                <Dialog open={openDialog}>
                    <DialogTitle>{t('new_document')}</DialogTitle>
                    <DialogContent className='d-flex flex-column gap-3'>
                        <Input
                            type='text'
                            required
                            className='d-flex justify-content-between'
                            name='formfile'
                            title={t('canvas_table_name')}
                            onChange={e => {
                                let value = e.target.value;
                                if (!value) {
                                    setError(true);
                                } else {
                                    setError(false);
                                    setFilename(e.target.value);
                                }
                            }}></Input>
                        <Input
                            type='file'
                            required
                            name='filename'
                            title=''
                            onChange={e => {
                                let fileList = e.target.files;
                                if (fileList === null || fileList.length == 0) {
                                    setError(true);
                                } else {
                                    setError(false);
                                    if (fileList[0].size > sizeLimit) {
                                        toastError(t('size_limit'))
                                    } else {
                                        setFormfile(fileList[0]);
                                    }
                                }
                            }}></Input>
                        {error && <span style={{ color: 'red' }}>{t('project_info_formik_required')}</span>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>{t('cancel_button')}</Button>
                        <Button disabled={!filename || !formfile} onClick={handleSaveDocument}>
                            {t('header_save_button')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default PanelDocument;
